import {ChatRegularOptionsIcon} from 'shells/icon/svg/chat-regular-options-icon';
import {EnvelopeNewIcon} from 'shells/icon/svg/envelope-new-icon';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {FeatureStatsCard} from 'shells/metrics/feature-stats/feature-stats-card';
import {NeedsSetupEmptyState} from 'shells/metrics/feature-stats/needs-setup-empty-state';
import {NeedsUseEmptyState} from 'shells/metrics/feature-stats/needs-use-empty-state';
import {Percentage} from 'shells/percentage/percentage';

import type {GetAudienceEngagementStats as GetAudienceEngagementStatsQuery} from 'nutshell-graphql-types';

import {useGetInboxStatus} from '../../../setup/text-messaging/graphql/hooks';
import {getIsEnrolled} from '../../../setup/text-messaging/helpers';

import {appHistory} from '../../../../history';

import {useGetSentBroadcastCount} from '../graphql/hooks/use-get-sent-broadcast-count';

import styles from './channel-campaign-breakdown.module.css';

type Props = {
    data: GetAudienceEngagementStatsQuery | undefined;
    isLoading: boolean;
};

export function ChannelCampaignBreakdown(props: Props) {
    const {inboxStatus, isLoading: isLoadingInboxStatus} = useGetInboxStatus();
    const {hasSentBroadcast, isLoading: isLoadingBroadcastCount} = useGetSentBroadcastCount();

    const engagementStats = props.data?.audience?.engagementStats;
    const smsEngagementStats = props.data?.audience?.smsEngagementStats;
    const isSmsEnrolled = getIsEnrolled(inboxStatus);
    const isLoadingEmailStats = props.isLoading || isLoadingBroadcastCount;
    const isLoadingSmsStats = props.isLoading || isLoadingInboxStatus;

    const emailSpecialStates = [
        {
            shouldRender: () => isLoadingEmailStats,
            content: (
                <div className='full-height flex flex-dir-col align-center justify-center'>
                    <LoadingPage padding={0} />
                </div>
            ),
        },
        {
            shouldRender: () => !hasSentBroadcast && !isLoadingBroadcastCount,
            content: (
                <NeedsSetupEmptyState
                    illustrationSrc='trial-onboarding/marketing-email-campaigns'
                    description='Configuring your DNS helps verify your domain and improve email deliverability. You can still send campaigns without it.'
                    learnMoreHref='https://www.nutshell.com/blog/upcoming-dmarc-email-authentication-requirements'
                    cta={{
                        label: 'Create an email broadcast',
                        onClick: () => {
                            appHistory.push('/marketing/email/broadcasts');
                        },
                    }}
                />
            ),
        },
        {
            shouldRender: () => engagementStats?.totalSentCount === 0 && !props.isLoading,
            content: (
                <NeedsUseEmptyState
                    illustrationSrc='trial-onboarding/marketing-email-campaigns'
                    title='No email campaigns have been sent.'
                    subtitle='Send an email broadcast to see engagement metrics here'
                    cta={{
                        label: 'Create an email broadcast',
                        onClick: () => {
                            appHistory.push('/marketing/email/broadcasts');
                        },
                    }}
                />
            ),
        },
    ];

    const smsSpecialStates = [
        {
            shouldRender: () => isLoadingSmsStats,
            content: (
                <div className='full-height flex flex-dir-col align-center justify-center'>
                    <LoadingPage padding={0} />
                </div>
            ),
        },
        {
            shouldRender: () => !isSmsEnrolled && !isLoadingInboxStatus,
            content: (
                <NeedsSetupEmptyState
                    illustrationSrc='trial-onboarding/engagement-sms'
                    description='To send SMS campaigns, you’ll need register your business for SMS. This enables messaging and ensures your texts are delivered properly.'
                    learnMoreHref='https://support.nutshell.com/en/articles/12921113-nutshell-sms-tips-for-a-successful-sms-enrollment'
                    cta={{
                        label: 'Register a phone number',
                        onClick: () => {
                            appHistory.push('/marketing/sms-setup');
                        },
                    }}
                />
            ),
        },
        {
            shouldRender: () => smsEngagementStats?.totalSentCount === 0 && !props.isLoading,
            content: (
                <NeedsUseEmptyState
                    illustrationSrc='trial-onboarding/engagement-sms'
                    title='No SMS campaigns have been sent.'
                    subtitle='Send an SMS broadcast to see engagement metrics here'
                    cta={{
                        label: 'Create an SMS broadcast',
                        onClick: () => {
                            appHistory.push('/marketing/sms/broadcasts');
                        },
                    }}
                />
            ),
        },
    ];

    const emailRows = [
        [
            {
                title: 'Messages sent',
                value: engagementStats?.totalSentCount.toLocaleString() ?? 0,
            },
        ],
        [
            {
                title: 'Open rate',
                // @ts-expect-error
                value: <Percentage value={engagementStats?.openRate} precision={2} />,
                secondaryValue: {
                    value: engagementStats?.openCount ?? 0,
                    nounSingular: 'open',
                    nounPlural: 'opens',
                },
            },
            {
                title: 'Click rate',
                // @ts-expect-error
                value: <Percentage value={engagementStats?.clickRate} precision={2} />,
                secondaryValue: {
                    value: engagementStats?.clickCount ?? 0,
                    nounSingular: 'click',
                    nounPlural: 'clicks',
                },
            },
            {
                title: 'Unsubscribed',
                value: (
                    // @ts-expect-error
                    <Percentage value={engagementStats?.unsubscribeRate} precision={2} />
                ),
                secondaryValue: {
                    value: engagementStats?.unsubscribeCount ?? 0,
                    nounSingular: 'bounce',
                    nounPlural: 'bounces',
                },
            },
        ],
    ];

    const smsRows = [
        [
            {
                title: 'Messages sent',
                value: smsEngagementStats?.totalSentCount.toLocaleString() ?? 0,
            },
        ],
        [
            {
                title: 'Reply rate',
                // @ts-expect-error
                value: <Percentage value={smsEngagementStats?.responseRate} precision={2} />,
                secondaryValue: {
                    value: smsEngagementStats?.responseCount ?? 0,
                    nounSingular: 'reply',
                    nounPlural: 'replies',
                },
            },
            {
                title: 'Undelivered rate',
                value: (
                    // @ts-expect-error
                    <Percentage value={smsEngagementStats?.undeliveredRate} precision={2} />
                ),
                secondaryValue: {
                    value: smsEngagementStats?.undeliveredCount ?? 0,
                    nounSingular: 'undelivered',
                    nounPlural: 'undelivered',
                },
            },
            {
                title: 'Opted-out',
                // @ts-expect-error
                value: <Percentage value={smsEngagementStats?.optoutRate} precision={2} />,
                secondaryValue: {
                    value: smsEngagementStats?.optoutCount ?? 0,
                    nounSingular: 'recipient',
                    nounPlural: 'recipients',
                },
            },
        ],
    ];

    return (
        <div className={styles['feature-stats-card-container']}>
            <Stack
                isHorizontal={true}
                justifyContent='space-between'
                isFullWidth={true}
                isFullHeight={true}
                spacing={16}
            >
                <FeatureStatsCard
                    headerDetails={{title: 'Email campaigns', icon: EnvelopeNewIcon}}
                    rows={emailRows}
                    specialStates={emailSpecialStates}
                />
                <FeatureStatsCard
                    headerDetails={{title: 'SMS campaigns', icon: ChatRegularOptionsIcon}}
                    rows={smsRows}
                    specialStates={smsSpecialStates}
                />
            </Stack>
        </div>
    );
}
